/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft, faBriefcase, faBuilding, faMapMarkerAlt, 
    faClock, faHome, faGraduationCap, faUserTie, faDollarSign,
    faCalendarAlt, faCheckCircle, faExclamationTriangle, faLightbulb,
    faChartLine, faStar, faRocket, faBookOpen, faUsers, faHandshake,
    faEye, faUser, faGlobe, faCode, faCogs,
    faIndustry, faIdBadge, faEnvelope, faTools
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import axios from 'axios';
import '../styles/ModernJobDetails.css';
import AksharLogo from '../assets/FINAL LOGO AK.png';
import JobDetailsSkeleton from '../components/JobDetailsSkeleton';
import SignUpToViewJob from '../components/SignUpToViewJob';

// Classify whether an opportunity should be treated as a job or an internship.
// This is intentionally stricter so that proper jobs don't accidentally
// get shown with "Internship Type" and "Stipend" labels.
const classifyOpportunityType = (jobData, sourceHint = null) => {
    if (!jobData && !sourceHint) return 'job';

    // Simple salary signal – if we clearly have a salary style range,
    // prefer treating it as a job instead of internship.
    const salaryText = jobData?.salary_range
        ? String(jobData.salary_range).toLowerCase()
        : '';
    const hasSalarySignals =
        !!jobData?.salary_min ||
        !!jobData?.salary_max ||
        (salaryText && (salaryText.includes('yearly') || salaryText.includes('monthly')));

    // If the backend explicitly tells us it's a job, respect that.
    if (sourceHint === 'job') return 'job';

    // If backend says "internship" but there are strong salary signals
    // and no obvious "intern" wording, treat it as a job for display.
    // This fixes cases where a real job was accidentally stored as internship.
    if (sourceHint === 'internship' && hasSalarySignals) {
        const fieldsToInspectForIntern = [
            jobData.job_type,
            jobData.jobType,
            jobData.type,
            jobData.category,
            jobData.department,
            jobData.job_title,
            jobData.jobTitle,
            jobData.title
        ];

        const hasInternKeywordExplicit = fieldsToInspectForIntern.some(
            (value) => typeof value === 'string' && value.toLowerCase().includes('intern')
        );

        if (!hasInternKeywordExplicit) {
            return 'job';
        }
    }

    if (!jobData) return 'job';

    const fieldsToInspect = [
        jobData.job_type,
        jobData.jobType,
        jobData.type,
        jobData.category,
        jobData.department,
        jobData.job_title,
        jobData.jobTitle,
        jobData.title
    ];

    const hasInternKeyword = fieldsToInspect.some(
        (value) => typeof value === 'string' && value.toLowerCase().includes('intern')
    );

    const hasStipend =
        !!jobData.stipend_type ||
        !!jobData.stipend_amount ||
        !!jobData.stipend_currency ||
        (!!jobData.salary_range && String(jobData.salary_range).toLowerCase().includes('stipend'));

    const hasSalaryFields =
        !!jobData.salary_min ||
        !!jobData.salary_max ||
        (!!jobData.salary_range && !String(jobData.salary_range).toLowerCase().includes('stipend'));

    // Treat as internship only when it really looks like one:
    // - clear "intern" wording AND some stipend info and no explicit salary range
    if (hasInternKeyword && hasStipend && !hasSalaryFields) {
        return 'internship';
    }

    // Fallback: default to job so proper jobs don't get mislabeled
    return 'job';
};

const ModernJobDetails = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState(false);
    const [applied, setApplied] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [error, setError] = useState('');
    const [jobSource, setJobSource] = useState(null);
    const hasCheckedApplicationRef = useRef(false); // Prevent multiple checks

    const normalizeJobData = (data) => {
        if (!data) return null;

        const coerceStringList = (value) => {
            if (!value) return value;
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') return value;
            return value;
        };

        const buildSalaryRange = () => {
            const min = data.salary_min || data.salaryMin;
            const max = data.salary_max || data.salaryMax;
            const currency = data.salary_currency || data.currency;
            const period = data.salary_period || data.salaryPeriod;
            if (min || max) {
                return `${currency || 'USD'} ${min || '—'} - ${max || '—'}${period ? ` ${period}` : ''}`;
            }
            return data.salary_range || data.salary || data.salaryRange || data.stipend;
        };

        return {
            ...data,
            job_title: data.job_title || data.jobTitle || data.title,
            company_name: data.company_name || data.companyName || data.company,
            salary_range: buildSalaryRange(),
            remote_option: data.remote_option || data.remoteOption || data.workMode,
            job_type: data.job_type || data.jobType || data.type,
            experience_required: data.experience_required || data.experienceRequired,
            experience_level: data.experience_level || data.experienceLevel,
            required_skills: coerceStringList(data.required_skills || data.requiredSkills || data.skills),
            education_required: data.education_required || data.educationRequired,
            application_deadline: data.application_deadline || data.applicationDeadline || data.deadline,
            company_website: data.company_website || data.companyWebsite,
            location: data.location || data.job_location || data.jobLocation,
            description: data.description || data.job_description || data.jobDescription || data.summary || data.about,
            responsibilities: data.responsibilities || data.job_responsibilities || data.jobResponsibilities,
            requirements: data.requirements || data.job_requirements || data.jobRequirements,
            benefits: coerceStringList(data.benefits || data.job_benefits || data.jobBenefits),
            skills: coerceStringList(data.skills || data.required_skills || data.requiredSkills),
            internship_type: data.internship_type || data.internshipType || data.job_type || data.jobType,
            duration: data.duration || data.internship_duration || data.contract_duration,
            stipend_type: data.stipend_type || data.stipendType,
            stipend_amount: data.stipend_amount || data.stipendAmount,
            stipend_currency: data.stipend_currency || data.stipendCurrency,
            eligibility: data.eligibility,
            openings: data.openings || data.numberOfPositions,
            vacancies: data.vacancies || data.openings || data.numberOfPositions,
            industry: data.industry,
            department: data.department,
            learning_opportunities: data.learningOpportunities,
            mentorship: data.mentorship,
            certificate: data.certificate,
            job_offer_possible: data.jobOfferPossible,
            start_date: data.startDate || data.expectedStartDate,
            end_date: data.endDate,
            country: data.country,
            city: data.city,
            work_mode: data.workMode || data.work_mode,
            working_days: data.workingDays,
            working_hours: data.workingHours || data.workHours,
            tools: coerceStringList(data.tools),
            perks: coerceStringList(data.perks),
            contact_email: data.contactEmail || data.contact_email || data.officialEmail,
            hr_name: data.hrName || data.hr_name || data.recruiterFullName,
            posted_at: data.postedAt || data.createdAt || data.created_at,
            company_description: data.companyDescription || data.company_description || data.company_overview,
            company_linkedin: data.companyLinkedIn || data.company_linkedin,
            country: data.country,
            state: data.state,
            preferred_joining_date: data.preferred_joining_date || data.preferredJoiningDate,
            apply_method: data.apply_method || data.applyMethod,
            office_address: data.office_address || data.officeAddress,
            postal_code: data.postal_code || data.postalCode,
            salary_min: data.salary_min || data.salaryMin,
            salary_max: data.salary_max || data.salaryMax,
            salary_currency: data.salary_currency || data.salaryCurrency || data.currency,
            salary_period: data.salary_period || data.salaryPeriod,
            salary_type: data.salary_type || data.salaryType,
            summary: data.summary || data.description,
            desired_profile: data.desiredProfile || data.requirements,
            visibility: data.visibility,
            // Privacy flags (default to showing values unless explicitly hidden)
            show_company_name: (
                typeof data.show_company_name !== 'undefined'
                    ? data.show_company_name
                    : (typeof data.showCompanyName !== 'undefined'
                        ? data.showCompanyName
                        : true)
            ),
            show_hr_name: (
                typeof data.show_hr_name !== 'undefined'
                    ? data.show_hr_name
                    : (typeof data.showHrName !== 'undefined'
                        ? data.showHrName
                        : true)
            ),
            _id: data._id || data.id
        };
    };

    const opportunityType = classifyOpportunityType(job, jobSource);
    const detailsLabel =
        opportunityType === 'internship'
            ? 'Modern Internship Details'
            : 'Modern Job Details';
    const isInternshipPage = opportunityType === 'internship';

    useEffect(() => {
        document.title = detailsLabel;
    }, [detailsLabel]);

    useEffect(() => {
        // Reset applied state when jobId changes
        setApplied(false);
        hasCheckedApplicationRef.current = false;
        
        if (jobId) {
            fetchJobDetails();
            checkApplicationStatus();
        }
    }, [jobId, user]);

    const getDisplayCompanyName = (jobData) => {
        if (!jobData || typeof jobData !== 'object') return '—';
        const hide =
            jobData.show_company_name === false ||
            jobData.showCompanyName === false;
        if (hide) {
            return 'Confidential';
        }
        return jobData.company_name || jobData.companyName || jobData.company || '—';
    };

    const getPublicHrName = (jobData) => {
        if (!jobData || typeof jobData !== 'object') return '';
        const hide =
            jobData.show_hr_name === false ||
            jobData.showHrName === false;
        if (hide) {
            return '';
        }
        return jobData.hr_name || '';
    };

    // Check if error is due to token expiration and redirect to login
    useEffect(() => {
        if (error) {
            const errorLower = error.toLowerCase();
            if (errorLower.includes('token') && errorLower.includes('expired') ||
                errorLower.includes('unauthorized') ||
                errorLower.includes('token has expired')) {
                const currentPath = window.location.pathname;
                navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
            }
        }
    }, [error, navigate]);

    const fetchJobDetails = async () => {
        // Use fetch API instead of axios to avoid global interceptor that redirects on 401
        // Job details endpoint is public and doesn't require authentication
        const attemptFetch = async (endpoint) => {
            try {
                const url = buildApiUrl(endpoint);
                // Job details are public - do NOT send auth header to avoid 401 from expired/invalid tokens
                // This ensures authenticated users always get job details even if their token has issues
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    if (response.status === 404 || response.status === 400) {
                        return null;
                    }
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                return await response.json();
            } catch (err) {
                // Network errors or other issues
                console.error(`Error fetching ${endpoint}:`, err);
                return null;
            }
        };

        try {
            setLoading(true);
            setError('');

            const jobResponse = await attemptFetch(`/api/jobs/${jobId}`);
            if (jobResponse) {
                setJob(normalizeJobData(jobResponse));
                setJobSource('job');
                return;
            }

            const internshipResponse = await attemptFetch(`/api/intern/internships/${jobId}`);
            if (internshipResponse) {
                setJob(normalizeJobData(internshipResponse));
                setJobSource('internship');
                return;
            }

            setError('Unable to find this opportunity. It may have been removed.');
        } catch (primaryError) {
            console.error('Error fetching job details:', primaryError);
            setError('Failed to load job details. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const checkApplicationStatus = async () => {
        if (!user?.userId || !jobId) return;
        
        // Prevent multiple simultaneous checks
        if (hasCheckedApplicationRef.current) return;
        hasCheckedApplicationRef.current = true;
        
        try {
            const response = await axios.get(buildApiUrl('/api/applications/my-applications'), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            
            // Ensure response.data is an array
            const applications = Array.isArray(response.data) ? response.data : (response.data?.applications || []);
            
            // Strict matching: check multiple possible ID fields
            const userApplication = applications.find(app => {
                const appJobId = app.job_id || app.jobId || app.job?._id || app.job || app.job_id?._id;
                return String(appJobId) === String(jobId);
            });
            
            // Only set applied if we found a valid application
            if (userApplication && userApplication._id) {
                setApplied(true);
                console.log('✅ Found existing application for job:', jobId);
            } else {
                setApplied(false);
                console.log('ℹ️ No existing application found for job:', jobId);
            }
        } catch (error) {
            console.error('Error checking application status:', error);
            
            // Check if token has expired - don't redirect here, just reset state
            // User will be redirected when they try to apply
            const errorMessage = error.response?.data?.error || error.message || '';
            if (error.response?.status === 401 || 
                (errorMessage.toLowerCase().includes('token') && errorMessage.toLowerCase().includes('expired')) ||
                errorMessage.toLowerCase().includes('unauthorized')) {
                setApplied(false);
                return;
            }
            
            setApplied(false); // Reset on error to prevent false positives
        } finally {
            // Allow re-checking after a delay (e.g., if user applies)
            setTimeout(() => {
                hasCheckedApplicationRef.current = false;
            }, 1000);
        }
    };

    const handleApply = async () => {
        if (!user?.userId) {
            navigate('/login');
            return;
        }

        setApplying(true);
        setError('');
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(buildApiUrl('/api/applications/apply'), {
                job_id: jobId,
                cover_letter: `I am very interested in the ${job?.job_title} position at ${job?.company_name}. I believe my skills and experience make me a strong candidate for this role.`
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 201) {
                setApplied(true);
                hasCheckedApplicationRef.current = true; // Mark as checked
                // Show success message
                alert('Application submitted successfully!');
            } else {
                setApplied(false);
            }
        } catch (error) {
            console.error('Error applying for job:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to apply for job. Please try again.';
            
            // Check if token has expired
            if (error.response?.status === 401 || 
                errorMessage.toLowerCase().includes('token') && errorMessage.toLowerCase().includes('expired') ||
                errorMessage.toLowerCase().includes('unauthorized')) {
                // Redirect to login with return URL
                const currentPath = window.location.pathname;
                navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
                return;
            }
            
            setError(errorMessage);
        } finally {
            setApplying(false);
        }
    };

    const toggleFavorite = () => {
        setIsFavorite(!isFavorite);
        // TODO: Implement favorite functionality
    };

    const shareJob = () => {
        if (!job) {
            alert('Job information is still loading. Please try again in a moment.');
            return;
        }

        const jobUrl = window.location.href;
        const messageParts = [
            '🚀 Check out this job opportunity!',
            `${job.job_title || 'Job Title'} at ${getDisplayCompanyName(job) || 'a great company'}`,
            job.location ? `📍 Location: ${job.location}` : null,
            job.salary_range ? `💰 Compensation: ${job.salary_range}` : null,
        ].filter(Boolean);

        messageParts.push(`🔗 Apply here: ${jobUrl}`);

        const whatsappUrl = 'https://api.whatsapp.com/send?text=';
        window.open(
            whatsappUrl + encodeURIComponent(messageParts.join('\n')),
            '_blank',
            'noopener,noreferrer'
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const parseList = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value
                .map(item => (typeof item === 'string' ? item.trim() : item))
                .filter(Boolean);
        }
        if (typeof value === 'string') {
            const normalized = value.replace(/\r/g, '\n');
            const delimiter = normalized.includes('\n') ? '\n' : ',';
            return normalized
                .split(delimiter)
                .map(entry => entry.replace(/^[•\-]\s*/, '').trim())
                .filter(Boolean);
        }
        return [];
    };

    const formatNumberValue = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const numeric = Number(value);
        if (Number.isNaN(numeric)) {
            return String(value);
        }
        return numeric.toLocaleString();
    };

    const formatSalaryRangeValue = () => {
        if (!job) return 'Not specified';
        if (job.salary_min || job.salary_max) {
            const currency = job.salary_currency || 'USD';
            const min = formatNumberValue(job.salary_min) || '—';
            const max = formatNumberValue(job.salary_max) || '—';
            const period = job.salary_period ? ` ${job.salary_period}` : '';
            return `${currency} ${min} - ${currency} ${max}${period}`;
        }
        return formatDisplayValue(job.salary_range);
    };

    const filterDetails = (details) =>
        details.filter((detail) => detail.value && detail.value !== 'Not specified');

    const formatDisplayValue = (value, fallback = 'Not specified') => {
        if (Array.isArray(value)) {
            return value.length ? value.join(', ') : fallback;
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (value === 0) return '0';
        return value ? value : fallback;
    };

    const formatLocation = () => {
        if (job?.city || job?.country) {
            return [job.city, job.country].filter(Boolean).join(', ');
        }
        return formatDisplayValue(job?.location);
    };

    const formatStipend = () => {
        if (job?.stipend_type) {
            if (job.stipend_type.toLowerCase() === 'unpaid') return 'Unpaid';
            if (job.stipend_amount) {
                return `${job.stipend_currency || 'USD'} ${job.stipend_amount}`;
            }
            return job.stipend_type;
        }
        if (job?.salary_range) return job.salary_range;
        return 'Not specified';
    };

    const internshipMetaDetails = job ? [
        {
            icon: faMapMarkerAlt,
            label: 'Location',
            value: formatLocation()
        },
        {
            icon: faBriefcase,
            label: 'Internship Type',
            value: formatDisplayValue(job.internship_type || job.job_type || job.jobType)
        },
        {
            icon: faHome,
            label: 'Work Mode',
            value: formatDisplayValue(job.work_mode || job.remote_option || job.remoteOption)
        },
        {
            icon: faClock,
            label: 'Duration',
            value: formatDisplayValue(job.duration)
        },
        {
            icon: faDollarSign,
            label: 'Stipend',
            value: formatStipend()
        },
        {
            icon: faGraduationCap,
            label: 'Eligibility',
            value: formatDisplayValue(job.eligibility || job.education_required)
        },
        {
            icon: faUserTie,
            label: 'Experience Required',
            value: formatDisplayValue(job.experience_required)
        },
        {
            icon: faIndustry,
            label: 'Industry',
            value: formatDisplayValue(job.industry)
        },
        {
            icon: faUsers,
            label: 'Openings',
            value: job.openings ? `${job.openings} positions` : formatDisplayValue(job.numberOfPositions)
        },
        {
            icon: faCalendarAlt,
            label: 'Start Date',
            value: formatDate(job.start_date)
        },
        {
            icon: faCalendarAlt,
            label: 'End Date',
            value: formatDate(job.end_date)
        },
        {
            icon: faCalendarAlt,
            label: 'Application Deadline',
            value: formatDate(job.application_deadline)
        },
        {
            icon: faClock,
            label: 'Working Days',
            value: formatDisplayValue(job.working_days)
        },
        {
            icon: faClock,
            label: 'Working Hours',
            value: formatDisplayValue(job.working_hours)
        },
        {
            icon: faIdBadge,
            label: 'Hiring Manager',
            value: formatDisplayValue(getPublicHrName(job))
        }
    ] : [];

    const jobMetaDetails = job ? [
        {
            icon: faMapMarkerAlt,
            label: 'Location',
            value: formatLocation()
        },
        {
            icon: faBriefcase,
            label: 'Job Type',
            value: formatDisplayValue(job.job_type || job.jobType || job.employment_type)
        },
        {
            icon: faHome,
            label: 'Work Mode',
            value: formatDisplayValue(job.work_mode || job.remote_option || job.remoteOption)
        },
        {
            icon: faChartLine,
            label: 'Experience Level',
            value: formatDisplayValue(job.experience_level || job.experience_required)
        },
        {
            icon: faUserTie,
            label: 'Experience Required',
            value: formatDisplayValue(job.experience_required)
        },
        {
            icon: faDollarSign,
            label: 'Salary Range',
            value: formatSalaryRangeValue()
        },
        {
            icon: faGraduationCap,
            label: 'Education',
            value: formatDisplayValue(job.education_required || job.eligibility)
        },
        {
            icon: faIndustry,
            label: 'Industry',
            value: formatDisplayValue(job.industry || job.department)
        },
        {
            icon: faBuilding,
            label: 'Department',
            value: formatDisplayValue(job.department)
        },
        {
            icon: faUsers,
            label: 'Openings',
            value: job.vacancies ? `${job.vacancies} positions` : formatDisplayValue(job.openings || job.numberOfPositions)
        },
        {
            icon: faCalendarAlt,
            label: 'Application Deadline',
            value: formatDate(job.application_deadline)
        },
        {
            icon: faIdBadge,
            label: 'Hiring Manager',
            value: formatDisplayValue(getPublicHrName(job))
        }
    ] : [];

    const applicationDetails = job ? [
        { label: 'Apply Method', value: formatDisplayValue(job.apply_method) },
        { label: 'Preferred Joining Date', value: formatDate(job.preferred_joining_date) },
        { label: 'Hiring Manager', value: formatDisplayValue(getPublicHrName(job)) },
        { label: 'Application Deadline', value: formatDate(job.application_deadline) }
    ] : [];

    const metaDetails = isInternshipPage ? internshipMetaDetails : jobMetaDetails;

    const visibleApplicationDetails = filterDetails(applicationDetails);

    const applicantCount = job?.applicants?.length || 0;
    const recordedViews = Number(job?.views) || 0;
    const displayViews = Math.max(recordedViews, applicantCount * 2);

    const skills = parseList(job?.required_skills || job?.skills);
    const responsibilities = parseList(job?.responsibilities);
    const requirementsList = parseList(job?.requirements || job?.desired_profile);
    const toolsList = parseList(job?.tools);
    const benefitsList = parseList(job?.benefits);
    const perksList = parseList(job?.perks);

    const hasBenefitsSection = benefitsList.length > 0 || perksList.length > 0;

    if (loading) {
        return <JobDetailsSkeleton />;
    }

    if (error || !job) {
        // Don't show error if it's a token expiration (will redirect)
        const errorLower = error?.toLowerCase() || '';
        if (errorLower.includes('token') && errorLower.includes('expired') ||
            errorLower.includes('unauthorized') ||
            errorLower.includes('token has expired')) {
            return null; // Will redirect in useEffect
        }
        
        // Authenticated users: show "job not found" with link to browse jobs (not signup prompt)
        if (user?.userId) {
            return (
                <div className="modern-job-details" style={{ padding: '2rem', textAlign: 'center' }}>
                    <div className="job-header" style={{ marginBottom: '2rem' }}>
                        <div className="header-content">
                            <div className="akshar-branding">
                                <img src={AksharLogo} alt="AksharJobs" className="branding-logo" />
                                <div className="branding-name">AksharJobs</div>
                            </div>
                        </div>
                    </div>
                    <div style={{ maxWidth: '400px', margin: '0 auto', background: '#fff', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>Job Not Found</h2>
                        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                            {error || 'This opportunity could not be loaded. It may have been removed or the link may be incorrect.'}
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => navigate('/jobseeker-dashboard')}
                            style={{ marginRight: '0.5rem' }}
                        >
                            Browse Jobs
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            );
        }
        
        // Unauthenticated users: show sign-up prompt
        return <SignUpToViewJob />;
    }

    const sanitizeJobTitle = (title) => {
        if (!title) return title;
        return String(title).replace(/^Updated\s*[-:]?\s*/i, "");
    };

    return (
        <div className="modern-job-details">
            <div className="job-header">
                <div className="header-content">
                    <div className="job-title-section">
                        <h1>{sanitizeJobTitle(job.job_title || job.jobTitle)}</h1>
                        <div className="company-info">
                            <span className="company-name" style={{ color: '#ffffff' }}>
                                {job.company_name || job.companyName}
                            </span>
                        </div>
                    </div>

                    <div className="job-actions">
                        <div className="akshar-branding">
                            <img src={AksharLogo} alt="AksharJobs logo" className="branding-logo" />
                            <div className="branding-name">AksharJobs</div>
                            <div className="branding-tagline">CONNECT | DISCOVER | ELEVATE</div>
                            {!user?.userId && (
                                <button
                                    type="button"
                                    className="branding-home-button"
                                    onClick={() => navigate('/')}
                                >
                                    <FontAwesomeIcon icon={faHome} />
                                    <span>Home</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="job-content">
                {/* Main Job Information */}
                <div className="job-main-content">
                    {/* Job Meta Information */}
                    <div className="job-meta-grid">
                        {metaDetails.map((detail, index) => (
                            <div className="meta-item" key={`${detail.label}-${index}`}>
                                <FontAwesomeIcon icon={detail.icon} />
                                <div className="meta-content">
                                    <span className="meta-label">{detail.label}</span>
                                    <span className="meta-value">{detail.value}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Job Description */}
                    <div className="job-section">
                        <h3>
                            <FontAwesomeIcon icon={faBriefcase} />
                            Job Description
                        </h3>
                        <div className="section-content">
                            <p className="multiline-text">{formatDisplayValue(job.description)}</p>
                        </div>
                    </div>

                    {requirementsList.length > 0 && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faUserTie} />
                                Desired Candidate Profile
                            </h3>
                            <div className="section-content">
                                <ul className="responsibilities-list">
                                    {requirementsList.map((requirement, index) => (
                                        <li key={`requirement-${index}`}>{requirement}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Learning Opportunities */}
                    {job.learning_opportunities && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faBookOpen} />
                                Learning Opportunities
                            </h3>
                            <div className="section-content">
                                <p className="multiline-text">{job.learning_opportunities}</p>
                            </div>
                        </div>
                    )}

                    {/* Program Highlights */}
                    {(job.mentorship || job.certificate || job.job_offer_possible) && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faLightbulb} />
                                Program Highlights
                            </h3>
                            <div className="highlight-badges">
                                {job.mentorship && (
                                    <span className="highlight-badge">Mentorship Provided</span>
                                )}
                                {job.certificate && (
                                    <span className="highlight-badge">Certificate Awarded</span>
                                )}
                                {job.job_offer_possible && (
                                    <span className="highlight-badge">Job Offer Considered</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Company Overview */}
                    {job.company_description && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faBuilding} />
                                About the Company
                            </h3>
                            <div className="section-content">
                                <p className="multiline-text">{job.company_description}</p>
                            </div>
                        </div>
                    )}

                    {/* Key Responsibilities */}
                    {responsibilities.length > 0 && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faCogs} />
                                Key Responsibilities
                            </h3>
                            <div className="section-content">
                                <ul className="responsibilities-list">
                                    {responsibilities.map((responsibility, index) => (
                                        <li key={index}>{responsibility}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Required Skills */}
                    {skills.length > 0 && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faCode} />
                                Required Skills
                            </h3>
                            <div className="section-content">
                                <div className="skills-grid">
                                    {skills.map((skill, index) => (
                                        <span key={index} className="skill-tag">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {toolsList.length > 0 && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faTools} />
                                Tools & Technologies
                            </h3>
                            <div className="section-content">
                                <div className="skills-grid">
                                    {toolsList.map((tool, index) => (
                                        <span key={`tool-${index}`} className="skill-tag">
                                            {tool}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {hasBenefitsSection && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faStar} />
                                Benefits & Perks
                            </h3>
                            {benefitsList.length > 0 && (
                                <div className="chip-list">
                                    {benefitsList.map((benefit, index) => (
                                        <span key={`benefit-${index}`} className="chip">
                                            {benefit}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {perksList.length > 0 && (
                                <>
                                    <p className="section-subtitle" style={{ marginTop: '1rem' }}>Perks</p>
                                    <div className="chip-list subtle">
                                        {perksList.map((perk, index) => (
                                            <span key={`perk-${index}`} className="chip subtle">
                                                {perk}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {visibleApplicationDetails.length > 0 && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faEnvelope} />
                                Application Details
                            </h3>
                            <div className="info-grid">
                                {visibleApplicationDetails.map((detail) => (
                                    <div className="info-item" key={detail.label}>
                                        <span className="info-label">{detail.label}</span>
                                        {detail.isEmail && detail.value && detail.value !== 'Not specified' ? (
                                            <a href={`mailto:${detail.value}`} className="info-value info-link">
                                                {detail.value}
                                            </a>
                                        ) : (
                                            <span className="info-value">{detail.value}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Company Culture */}
                    {job.company_culture && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faUsers} />
                                Company Culture
                            </h3>
                            <div className="section-content">
                                <p className="multiline-text">{job.company_culture}</p>
                            </div>
                        </div>
                    )}

                    {/* Application Process */}
                    {job.application_process && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faHandshake} />
                                Application Process
                            </h3>
                            <div className="section-content">
                                <p className="multiline-text">{job.application_process}</p>
                            </div>
                        </div>
                    )}

                    {/* Additional Requirements */}
                    {job.additional_requirements && (
                        <div className="job-section">
                            <h3>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                Additional Requirements
                            </h3>
                            <div className="section-content">
                                <p className="multiline-text">{job.additional_requirements}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="job-sidebar">
                    {/* Application Section */}
                    <div className="application-card">
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        
                        {applied ? (
                            <div className="applied-status">
                                <FontAwesomeIcon icon={faCheckCircle} className="applied-icon" />
                                <h4>Application Submitted</h4>
                                <p>You have successfully applied for this position.</p>
                                <button 
                                    onClick={() => navigate('/application-tracker')}
                                    className="view-applications-button"
                                >
                                    View My Applications
                                </button>
                            </div>
                        ) : (
                            <div className="apply-section">
                                <h4>Ready to Apply?</h4>
                                <p>Join AksharJobs Network and make an impact on your career.</p>
                                <button 
                                    onClick={handleApply}
                                    disabled={applying}
                                    className="apply-button"
                                >
                                    {applying ? (
                                        <>
                                            <div className="button-spinner"></div>
                                            Applying...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faRocket} />
                                            Apply Now
                                        </>
                                    )}
                                </button>
                                <p className="application-note">
                                    Your application will be reviewed by the hiring team.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Job Stats */}
                    <div className="job-stats-card">
                        <h4>Job Statistics</h4>
                        <div className="job-stats-grid">
                            <div className="job-stat-item">
                                <div className="job-stat-icon">
                                    <FontAwesomeIcon icon={faEye} />
                                </div>
                                <div className="job-stat-content">
                                            <span className="job-stat-value">{displayViews}</span>
                                    <span className="job-stat-label">Views</span>
                                </div>
                            </div>
                            <div className="job-stat-item">
                                <div className="job-stat-icon">
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                                <div className="job-stat-content">
                                    <span className="job-stat-value">
                                        {job.visibleApplications !== undefined 
                                            ? job.visibleApplications
                                            : (job.applicants?.length || 0)
                                        }
                                    </span>
                                    <span className="job-stat-label">Applicants</span>
                                </div>
                            </div>
                            <div className="job-stat-item">
                                <div className="job-stat-icon">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                                <div className="job-stat-content">
                                    <span className="job-stat-value job-stat-date">{formatDate(job.posted_at)}</span>
                                    <span className="job-stat-label">Posted</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="quick-actions-card">
                        <h4>Quick Actions</h4>
                        <div className="job-actions-list">
                            <button 
                                onClick={toggleFavorite}
                                    className={`job-action-button ${isFavorite ? 'favorited' : ''}`}
                            >
                                    <FontAwesomeIcon icon={faStar} />
                                {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                            </button>
                            <button onClick={shareJob} className="job-action-button">
                                    <FontAwesomeIcon icon={faGlobe} />
                                Share Job
                            </button>
                            <button 
                                onClick={() => navigate('/jobs')}
                                className="job-action-button"
                            >
                                <FontAwesomeIcon icon={faBriefcase} />
                                Browse More Jobs
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModernJobDetails;
